// Virtual try-on: MediaPipe Face Landmarker (CDN, on demand) tracks the user's
// face and overlays a clean, pre-cut transparent PNG of the frame at the eye
// line. Clean cutouts are produced offline (_products/batch_tryon.py) and
// shipped as theme assets, indexed by product handle in tryon-manifest.json.
// If a product has no clean cutout the overlay is not rendered (no broken
// chroma-key fallback). Supports live variant switching, an in-modal "shape"
// strip, photo capture with brand watermark + Web Share, and a guidance pill.

type TryOnMode = 'variants' | 'metafield' | 'single'

interface VariantEntry { id: number | string; label: string; image: string; clean?: string }
interface MateEntry { handle: string; title: string; image: string; thumb: string; clean?: string }
interface TryOnData {
  mode: TryOnMode
  productTitle: string
  productHandle: string
  productImage: string
  productClean?: string
  manifestUrl?: string
  frameWidthMm: number
  variants: VariantEntry[]
  shapeMates: MateEntry[]
}

interface Landmark { x: number; y: number; z: number }

let vision: any = null
let landmarker: any = null

const spriteCache = new Map<string, HTMLCanvasElement | Promise<HTMLCanvasElement>>()
// URLs known to be pre-cut transparent PNGs — these are drawn raw.
const cleanUrls = new Set<string>()

let manifestPromise: Promise<Record<string, string>> | null = null

// Fetch + cache the handle -> filename manifest of clean cutouts.
function loadManifest(url?: string): Promise<Record<string, string>> {
  if (!url) return Promise.resolve({})
  if (manifestPromise) return manifestPromise
  manifestPromise = fetch(url)
    .then((r) => (r.ok ? r.json() : {}))
    .catch(() => ({}))
  return manifestPromise
}

// Resolve a product handle to its clean-cutout URL using the manifest. Asset
// URLs share the manifest's directory, so swap the filename on its path.
async function cleanForHandle(handle: string, manifestUrl?: string): Promise<string> {
  if (!handle || !manifestUrl) return ''
  const manifest = await loadManifest(manifestUrl)
  const file = manifest[handle]
  if (!file) return ''
  const base = manifestUrl.split('?')[0].replace(/[^/]*$/, '')
  const resolved = base + file
  cleanUrls.add(resolved)
  return resolved
}

export function initTryOn(): void {
  const btn = document.querySelector<HTMLButtonElement>('[data-tryon-open]')
  const dataEl = document.querySelector<HTMLScriptElement>('[data-tryon-data]')
  if (!btn || !dataEl) return
  let data: TryOnData
  try {
    data = JSON.parse(dataEl.textContent ?? '{}') as TryOnData
  } catch {
    return
  }
  btn.addEventListener('click', () => openTryOn(data))
}

async function openTryOn(data: TryOnData): Promise<void> {
  const firstVariant = data.variants[0]
  const initialLabel = firstVariant?.label ?? ''
  // One clean cutout per product (color switching keeps the same cutout in
  // v1). Per-variant metafield clean wins; else the manifest cutout.
  const variantClean = firstVariant?.clean
  let currentClean = ''
  if (variantClean && variantClean.length > 0) {
    cleanUrls.add(variantClean)
    currentClean = variantClean
  } else {
    currentClean = await cleanForHandle(data.productHandle, data.manifestUrl)
  }
  const initialImage = currentClean

  const lastFocused = document.activeElement as HTMLElement | null
  const modal = buildModal(data, initialLabel)
  document.body.appendChild(modal)
  document.body.style.overflow = 'hidden'
  requestAnimationFrame(() => modal.classList.add('is-open'))
  // Move focus into the modal so SR/keyboard users land there.
  const closeBtn = modal.querySelector<HTMLButtonElement>('[data-tryon-close]')
  closeBtn?.focus()

  const stage = modal.querySelector<HTMLElement>('[data-tryon-stage]')!
  const status = modal.querySelector<HTMLElement>('[data-tryon-status]')!
  const guide = modal.querySelector<HTMLElement>('[data-tryon-guide]')!
  const video = modal.querySelector<HTMLVideoElement>('[data-tryon-video]')!
  const canvas = modal.querySelector<HTMLCanvasElement>('[data-tryon-canvas]')!
  const snapBtn = modal.querySelector<HTMLButtonElement>('[data-tryon-snap]')!
  const labelEl = modal.querySelector<HTMLElement>('[data-tryon-current]')!
  const ctx = canvas.getContext('2d')!

  const state = {
    stream: null as MediaStream | null,
    raf: 0,
    closed: false,
    activeImage: initialImage,
    activeLabel: initialLabel,
    lastDetectionAt: 0,
    centerHistory: [] as Array<{ x: number; y: number }>,
    capturing: false,
  }

  const close = (): void => {
    state.closed = true
    if (state.raf) cancelAnimationFrame(state.raf)
    state.stream?.getTracks().forEach((t) => t.stop())
    document.body.style.overflow = ''
    modal.classList.remove('is-open')
    setTimeout(() => modal.remove(), 200)
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus()
  }
  modal.querySelector('[data-tryon-close]')!.addEventListener('click', close)
  modal.addEventListener('click', (e) => {
    if (e.target === modal) close()
  })
  // Trap Tab/Shift+Tab inside the modal so keyboard focus never escapes.
  const FOCUSABLE_SEL = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), [tabindex]:not([tabindex="-1"])'
  modal.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return
    const focusables = Array.from(modal.querySelectorAll<HTMLElement>(FOCUSABLE_SEL))
      .filter((el) => !el.hasAttribute('hidden') && el.offsetParent !== null)
    if (!focusables.length) return
    const first = focusables[0]!
    const last = focusables[focusables.length - 1]!
    const active = document.activeElement as HTMLElement | null
    if (e.shiftKey && (active === first || !modal.contains(active))) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && active === last) {
      e.preventDefault()
      first.focus()
    }
  })
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close()
  }
  document.addEventListener('keydown', onKey)
  modal.addEventListener('remove', () => document.removeEventListener('keydown', onKey))

  // Variant chip clicks → relabel. One cutout per product, so the overlay
  // image stays the same unless the variant has its own metafield cutout.
  modal.querySelectorAll<HTMLButtonElement>('[data-tryon-variant]').forEach((chip) => {
    chip.addEventListener('click', () => {
      const vc = chip.dataset.clean || ''
      const label = chip.dataset.label ?? ''
      modal.querySelectorAll<HTMLButtonElement>('[data-tryon-variant]').forEach((c) => {
        c.classList.toggle('is-selected', c === chip)
        c.setAttribute('aria-checked', c === chip ? 'true' : 'false')
      })
      if (vc) {
        cleanUrls.add(vc)
        currentClean = vc
        state.activeImage = vc
        getSprite(vc).catch(() => {})
      } else if (currentClean) {
        state.activeImage = currentClean
      }
      state.activeLabel = label
      labelEl.textContent = label
    })
  })

  // Shape-strip mate clicks → resolve that product's clean cutout.
  modal.querySelectorAll<HTMLButtonElement>('[data-tryon-mate]').forEach((mate) => {
    mate.addEventListener('click', async () => {
      const handle = mate.dataset.handle ?? ''
      const title = mate.dataset.title ?? ''
      modal.querySelectorAll<HTMLButtonElement>('[data-tryon-mate]').forEach((m) => {
        m.classList.toggle('is-selected', m === mate)
      })
      // Switching products clears variant selection (chips are the orig product)
      modal.querySelectorAll<HTMLButtonElement>('[data-tryon-variant]').forEach((c) => {
        c.classList.remove('is-selected')
        c.setAttribute('aria-checked', 'false')
      })
      state.activeLabel = title
      labelEl.textContent = title
      const clean = await cleanForHandle(handle, data.manifestUrl)
      if (state.closed) return
      currentClean = clean
      state.activeImage = clean
      if (clean) getSprite(clean).catch(() => {})
    })
  })

  try {
    status.textContent = 'Loading face tracking…'
    await ensureLandmarker()
    if (state.closed) return

    if (initialImage) {
      status.textContent = 'Preparing frame…'
      await getSprite(initialImage).catch(() => {})
      if (state.closed) return
    }

    status.textContent = 'Requesting camera…'
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
      audio: false,
    })
    if (state.closed) {
      stream.getTracks().forEach((t) => t.stop())
      return
    }
    state.stream = stream
    video.srcObject = stream
    await new Promise<void>((resolve) => {
      video.onloadedmetadata = () => video.play().then(() => resolve())
    })
    if (state.closed) return

    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    stage.classList.add('is-ready')
    status.textContent = ''
    snapBtn.disabled = false

    // Prefetch any per-variant metafield cutouts so switching is instant.
    const others = data.variants
      .map((v) => v.clean ?? '')
      .filter((u) => u && u !== initialImage)
    if (others.length) prefetchSprites(others)

    const loop = (): void => {
      if (state.closed) return

      ctx.save()
      ctx.scale(-1, 1)
      ctx.drawImage(video, -canvas.width, 0, canvas.width, canvas.height)
      ctx.restore()

      const sprite = spriteCache.get(state.activeImage)
      const result = landmarker.detectForVideo(video, performance.now())
      const lm: Landmark[] | undefined = result?.faceLandmarks?.[0]
      if (lm) {
        state.lastDetectionAt = performance.now()
        // MediaPipe Face Landmarker: 33 is the outer corner of the subject's
        // RIGHT eye (lives at raw-image-left, x≈0.3). 263 is the outer corner
        // of the subject's LEFT eye (raw-image-right, x≈0.7). We mirror the
        // video onto the canvas, so raw-right maps to canvas-left. To keep
        // angle = 0 for an upright face, use 263 as our canvas-left anchor.
        const left = lm[263]
        const right = lm[33]
        const lx = (1 - left.x) * canvas.width
        const ly = left.y * canvas.height
        const rx = (1 - right.x) * canvas.width
        const ry = right.y * canvas.height
        const cx = (lx + rx) / 2
        const cy = (ly + ry) / 2
        const eyeDist = Math.hypot(rx - lx, ry - ly)
        const angle = Math.atan2(ry - ly, rx - lx)

        // Zygomatic (cheekbone) width: LM 454 = raw-right, 234 = raw-left.
        // On the mirrored canvas these swap sides, but distance is the same.
        // Average adult face width here ≈ 140 mm → gives us a px/mm scale.
        const zl = lm[454]
        const zr = lm[234]
        const faceWidthPx = Math.hypot(
          (1 - zl.x - (1 - zr.x)) * canvas.width,
          (zl.y - zr.y) * canvas.height,
        )
        const pxPerMm = faceWidthPx / 140

        recordCenter(state.centerHistory, cx, cy)
        if (!state.activeImage) {
          guide.textContent = 'Try-on preview for this style is coming soon'
          guide.dataset.tone = 'info'
        } else {
          updateGuidance(guide, {
            centerX: cx / canvas.width,
            eyeRatio: eyeDist / canvas.width,
            jitter: jitterOf(state.centerHistory),
            detected: true,
          })
          if (sprite && sprite instanceof HTMLCanvasElement) {
            // Scale the cutout to the product's actual physical frame width.
            const drawW = data.frameWidthMm * pxPerMm
            const drawH = drawW * (sprite.height / sprite.width)
            ctx.save()
            ctx.translate(cx, cy - eyeDist * 0.04)
            ctx.rotate(angle)
            ctx.drawImage(sprite, -drawW / 2, -drawH / 2, drawW, drawH)
            ctx.restore()
          }
        }
      } else if (performance.now() - state.lastDetectionAt > 500) {
        updateGuidance(guide, { detected: false })
      }

      state.raf = requestAnimationFrame(loop)
    }
    loop()

    snapBtn.addEventListener('click', () => onCapture(canvas, data.productTitle, state.activeLabel, state))
  } catch (err) {
    console.error('[tryon]', err)
    const msg = err instanceof Error && err.message ? err.message : 'Camera access denied or unavailable.'
    status.textContent = msg
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Sprite cache (URL → chroma-keyed canvas)

function getSprite(url: string): Promise<HTMLCanvasElement> {
  const hit = spriteCache.get(url)
  if (hit instanceof HTMLCanvasElement) return Promise.resolve(hit)
  if (hit) return hit as Promise<HTMLCanvasElement>
  const p = loadSprite(url).then((c) => {
    spriteCache.set(url, c)
    return c
  })
  spriteCache.set(url, p)
  return p
}

function prefetchSprites(urls: string[]): void {
  let i = 0
  const idle = (window as any).requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 0))
  const next = (): void => {
    if (i >= urls.length) return
    const url = urls[i++]
    if (spriteCache.has(url)) {
      idle(next)
      return
    }
    getSprite(url)
      .catch(() => {})
      .finally(() => idle(next))
  }
  idle(next)
}

// Clean cutouts are pre-cut transparent PNGs — draw as-is, no processing.
async function loadSprite(url: string): Promise<HTMLCanvasElement> {
  const img = await loadImg(url)
  const c = document.createElement('canvas')
  c.width = img.naturalWidth
  c.height = img.naturalHeight
  c.getContext('2d')!.drawImage(img, 0, 0)
  return c
}

function loadImg(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Failed to load product image'))
    img.src = src
  })
}

// ────────────────────────────────────────────────────────────────────────────
// MediaPipe

async function ensureLandmarker(): Promise<void> {
  if (landmarker) return
  if (!vision) {
    // @ts-expect-error dynamic CDN import — Vite leaves this alone via vite-ignore
    vision = await import(/* @vite-ignore */ 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.9/vision_bundle.mjs')
  }
  const fileset = await vision.FilesetResolver.forVisionTasks(
    'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.9/wasm',
  )
  landmarker = await vision.FaceLandmarker.createFromOptions(fileset, {
    baseOptions: {
      modelAssetPath:
        'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task',
    },
    runningMode: 'VIDEO',
    numFaces: 1,
  })
}

// ────────────────────────────────────────────────────────────────────────────
// Guidance pill

interface GuidanceInput { centerX?: number; eyeRatio?: number; jitter?: number; detected: boolean }

function updateGuidance(el: HTMLElement, g: GuidanceInput): void {
  let msg = ''
  let tone: 'info' | 'good' = 'info'
  if (!g.detected) {
    msg = 'Center your face in the frame'
  } else if (g.centerX !== undefined && Math.abs(0.5 - g.centerX) > 0.18) {
    msg = 'Center your face'
  } else if (g.eyeRatio !== undefined && g.eyeRatio < 0.16) {
    msg = 'Move closer'
  } else if (g.eyeRatio !== undefined && g.eyeRatio > 0.4) {
    msg = 'Move back a touch'
  } else if (g.jitter !== undefined && g.jitter > 12) {
    msg = 'Hold steady'
  } else {
    msg = 'Looking great'
    tone = 'good'
  }
  if (el.textContent !== msg) el.textContent = msg
  el.dataset.tone = tone
}

function recordCenter(buf: Array<{ x: number; y: number }>, x: number, y: number): void {
  buf.push({ x, y })
  if (buf.length > 24) buf.shift()
}

function jitterOf(buf: Array<{ x: number; y: number }>): number {
  if (buf.length < 6) return 0
  let mx = 0
  let my = 0
  for (const p of buf) {
    mx += p.x
    my += p.y
  }
  mx /= buf.length
  my /= buf.length
  let s = 0
  for (const p of buf) s += Math.hypot(p.x - mx, p.y - my)
  return s / buf.length
}

// ────────────────────────────────────────────────────────────────────────────
// Capture + share

async function onCapture(
  src: HTMLCanvasElement,
  product: string,
  variantLabel: string,
  state: { capturing: boolean },
): Promise<void> {
  if (state.capturing) return
  state.capturing = true
  try {
    const out = document.createElement('canvas')
    out.width = src.width
    out.height = src.height
    const c = out.getContext('2d')!
    c.drawImage(src, 0, 0)
    drawWatermark(c, out.width, out.height, product, variantLabel)

    const blob = await new Promise<Blob | null>((res) => out.toBlob(res, 'image/png', 0.95))
    if (!blob) throw new Error('Could not encode photo')

    const file = new File([blob], `meera-tryon-${Date.now()}.png`, { type: 'image/png' })
    const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean }
    if (nav.share && nav.canShare?.({ files: [file] })) {
      try {
        await nav.share({
          files: [file],
          title: `Meera — ${product}`,
          text: `Trying ${product}${variantLabel ? ' · ' + variantLabel : ''}`,
        })
        return
      } catch {
        // user cancelled or share failed; fall through to download
      }
    }
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = file.name
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 5000)
  } catch (err) {
    console.error('[tryon] capture', err)
  } finally {
    state.capturing = false
  }
}

function drawWatermark(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  product: string,
  variant: string,
): void {
  const barH = Math.max(72, Math.round(h * 0.09))
  ctx.save()
  // Soft bone strip
  const grad = ctx.createLinearGradient(0, h - barH, 0, h)
  grad.addColorStop(0, 'rgba(244, 240, 232, 0)')
  grad.addColorStop(0.5, 'rgba(244, 240, 232, 0.78)')
  grad.addColorStop(1, 'rgba(244, 240, 232, 0.92)')
  ctx.fillStyle = grad
  ctx.fillRect(0, h - barH, w, barH)

  ctx.fillStyle = 'rgba(28, 24, 22, 0.92)'
  ctx.textBaseline = 'middle'

  const wordmarkSize = Math.round(barH * 0.34)
  ctx.font = `${wordmarkSize}px "Cormorant Garamond", "Times New Roman", serif`
  ctx.textAlign = 'left'
  ctx.fillText('MEERA', Math.round(w * 0.04), h - barH / 2)

  const metaSize = Math.round(barH * 0.18)
  ctx.font = `${metaSize}px "Inter", -apple-system, system-ui, sans-serif`
  ctx.textAlign = 'right'
  const right = w - Math.round(w * 0.04)
  ctx.fillText(product, right, h - barH * 0.62)
  if (variant) {
    ctx.fillStyle = 'rgba(28, 24, 22, 0.65)'
    ctx.fillText(variant, right, h - barH * 0.34)
  }
  ctx.restore()
}

// ────────────────────────────────────────────────────────────────────────────
// DOM

function buildModal(data: TryOnData, initialLabel: string): HTMLElement {
  const m = document.createElement('div')
  m.className = 'tryon-modal'
  m.setAttribute('role', 'dialog')
  m.setAttribute('aria-modal', 'true')
  m.setAttribute('aria-label', 'Virtual try-on')

  const variantsHtml = data.variants
    .map(
      (v, i) => `
      <button type="button"
        class="tryon-variant${i === 0 ? ' is-selected' : ''}"
        data-tryon-variant
        data-image="${escAttr(v.image)}"
        data-clean="${escAttr(v.clean ?? '')}"
        data-label="${escAttr(v.label)}"
        role="radio"
        aria-checked="${i === 0 ? 'true' : 'false'}"
        aria-label="${escAttr(v.label)}">
        <span class="tryon-variant__swatch" aria-hidden="true">
          <img src="${escAttr(v.image)}" alt="" loading="lazy" decoding="async">
        </span>
        <span class="tryon-variant__label t-small">${escHtml(v.label)}</span>
      </button>`,
    )
    .join('')

  const matesHtml = data.shapeMates
    .map(
      (mate) => `
      <button type="button"
        class="tryon-mate"
        data-tryon-mate
        data-handle="${escAttr(mate.handle)}"
        data-title="${escAttr(mate.title)}"
        aria-label="Try ${escAttr(mate.title)}">
        <img src="${escAttr(mate.thumb)}" alt="" loading="lazy" decoding="async">
        <span class="tryon-mate__label t-small">${escHtml(mate.title)}</span>
      </button>`,
    )
    .join('')

  m.innerHTML = `
    <div class="tryon-modal__inner">
      <button class="tryon-modal__close" data-tryon-close aria-label="Close try-on">×</button>

      <div class="tryon-modal__stage" data-tryon-stage>
        <video data-tryon-video playsinline muted></video>
        <canvas data-tryon-canvas></canvas>
        <p class="tryon-modal__guide" data-tryon-guide data-tone="info">Center your face in the frame</p>
        <p class="tryon-modal__status" data-tryon-status>Initializing…</p>
      </div>

      <aside class="tryon-modal__panel">
        <header class="tryon-modal__head">
          <p class="t-label tryon-modal__eyebrow">Virtual try-on</p>
          <h2 class="t-h3 tryon-modal__title">${escHtml(data.productTitle)}</h2>
          <p class="tryon-modal__active t-small" data-tryon-current>${escHtml(initialLabel)}</p>
        </header>

        ${
          data.variants.length > 1
            ? `<div class="tryon-modal__group">
                <p class="t-label tryon-modal__group-label">Try a colour</p>
                <div class="tryon-variants" role="radiogroup" aria-label="Colour">${variantsHtml}</div>
              </div>`
            : ''
        }

        ${
          data.shapeMates.length > 0
            ? `<div class="tryon-modal__group">
                <p class="t-label tryon-modal__group-label">Try another shape</p>
                <div class="tryon-mates">${matesHtml}</div>
              </div>`
            : ''
        }

        <div class="tryon-modal__actions">
          <button type="button" class="tryon-modal__snap t-label" data-tryon-snap disabled>
            <span class="tryon-modal__snap-dot" aria-hidden="true"></span>
            Save photo
          </button>
          <p class="tryon-modal__hint t-small">Beta · best with even lighting and your face centered.</p>
        </div>
      </aside>
    </div>
  `
  return m
}

function escHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function escAttr(s: string): string {
  return escHtml(s).replace(/"/g, '&quot;')
}
